import { createAction } from 'redux-actions';
import { bisector } from 'd3-array';
import { keys } from 'd3-collection';
import { isoFormat } from 'd3-time-format';
import { timeDay, timeMinute, timeMonth } from 'd3-time';

import {
  descriptionCreator,
  idCreator,
  metaCreator,
  payloadCreator,
} from './creator';
import fetch, {
  Api,
  defaultRequestConfig,
  getJson,
  handleError,
} from './fetch';
import { checkForCachedDataAction, requestAction } from './request';
import { timeSort } from '../utils/dataUtils';

export const nightscoutFetchAction = createAction('GET_NIGHTSCOUT_DATA', payloadCreator, metaCreator);

export const nightscoutStoreAction = createAction('POST_NIGHTSCOUT_DATA', payloadCreator, metaCreator);

const nightscoutDateFormat = date => date.getTime();

const bisectTime = bisector(timeSort).right;

const parseProfileSwitch = (json) => {
  const {
    duration,
    profile: name,
    profileJson,
    percentage,
    timeshift,
  } = json;
  let profile;
  try {
    profile = JSON.parse(profileJson);
  } catch (error) {
    // OK.
    // Could try to retrieve the profile from profiles, based on name...
  }
  const object = {
    type: 'profileChanged',
    name,
    duration,
  };
  if (profile) {
//    object.profile = profile.id;
    object.profile = profile;
  }
  if (timeshift) {
    object.timeshift = timeshift;
  }
  if (percentage && !Number.isNaN(percentage)) {
    object.percentage = percentage;
  }
  return object;
};

const collections = {
  entries: {
    dateField: 'date',
    dateFormat: nightscoutDateFormat,
  },
  treatments: {
    dateField: 'created_at',
    dateFormat: isoFormat,
  },
  /*
  devicestatus: {
    dateField: 'created_at',
    dateFormat: isoFormat,
  },
  */
};

const typeTranlations = {
  cal: {
    /* Calibration */
    collection: 'smbg',
    key: 'glucose',
  },
  carbs: {
    collection: 'nutrition',
    key: 'carbohydrate',
  },
  insulin: {
    collection: 'bolus',
    key: 'amount',
  },
  mbg: {
    /* When parsing, separate between types (manual or from meter) */
    collection: 'smbg',
    key: 'glucose',
  },
  notes: {
    collection: 'notes',
    key: 'note',
  },
  sgv: {
    /* When parsing, separate between types
     * (Dexcom / Medtronic / Libre log / Libre Glimp / Libre xDrip)
     */
    collection: 'sgv',
    key: 'glucose',
  },
};

// Known eventTypes:
// 'Announcement', 'Bolus Wizard', 'Insulin Change', 'Note', 'OpenAPS Offline',
// 'Profile Switch', 'Site Change', 'Temp Basal', 'Temporary Target'
const eventTypeParsers = {
  'Insulin Change': {
    collection: 'pumpEvents',
    value: { type: 'cartridgeChanged' },
  },
  Note: {
    collection: 'notes',
    value: (e) => {
      const { notes } = e;
      return {
        note: notes,
      };
    },
  },
  'OpenAPS Offline': {
    collection: 'pumpEvents',
    value: (e) => {
      const { duration } = e;
      return {
        type: 'apsOffline',
        duration,
      };
    },
  },
  'Profile Switch': {
    collection: 'pumpEvents',
    value: parseProfileSwitch,
  },
  'Site Change': {
    collection: 'pumpEvents',
    value: { type: 'cannulaChanged' },
  },
  'Temp Basal': {
    collection: 'basal',
    value: (e) => {
      const { absolute, duration, percent } = e;
      if (percent && !Number.isNaN(percent)) {
        return {
          duration,
          percent: 100 + percent,
        };
      }
      if (absolute) {
        return {
          duration,
          rate: absolute,
        };
      }
      // A note indicating the end / cancellation of a TBR
      return {
        end: true,
      };
    },
  },
  'Temporary Target': {
    collection: 'notes',
    value: (e) => {
      const { reason, notes, duration } = e;
      const object = {
        duration,
        note: notes || reason || 'Tempoarary target',
      };
      if (reason) {
        object.reason = reason;
      }
      return object;
    },
  },
};

export const supportedTypes = keys(collections);

const minDate = new Date(2000, 0, 1);
let maxDate;
// Allow the page to run for a long time...
const pushMaxDate = () => {
  maxDate = timeDay.offset(new Date(), 1);
  window.setTimeout(() => {
    pushMaxDate();
  }, 60 * 60 * 12);
};
pushMaxDate();

// eslint-disable-next-line no-underscore-dangle
const parseId = item => (item.id || item._id);

const parseTime = (item) => {
  // We consider dateString and sysTime to be the best options, as they encode time zone info too.
  let date = new Date(item.dateString);
  if (date > minDate && date < maxDate) {
    return date;
  }
  // else continue trying
  if (item.sysTime) {
    date = new Date(item.sysTime);
    if (date > minDate && date < maxDate) {
      return date;
    }
  }
  // else continue trying
  if (item.created_at) {
    date = new Date(item.created_at);
    if (date > minDate && date < maxDate) {
      return date;
    }
  }
  // else continue trying
  if (item.date) {
    date = new Date(item.date);
    if (date > minDate && date < maxDate) {
      return date;
    }
    // else continue trying
  }
  if (item.time) {
    date = new Date(item.time);
    if (date > minDate && date < maxDate) {
      return date;
    }
    // else continue trying
  }
  if (item.eventTime) {
    date = new Date(item.eventTime);
    if (date > minDate && date < maxDate) {
      return date;
    }
    // else continue trying
  }
  const error = new TypeError(`Unable to parse date, dateString ${
    item.dateString
  }, date ${
    item.date
  }, time ${
    item.time
  }, eventTime ${
    item.eventTime
  }, created_at ${
    item.created_at
  }, minDate ${
    minDate
  }, maxDate ${
    maxDate
  }`);
  error.item = item;
  throw error;
};

const itemAdapter = (item, store) => {
  const { eventType, unit } = item;
  const newStore = { ...store };
  const object = {
    id: parseId(item),
    time: parseTime(item).toISOString(),
    // raw: item, // TODO: only include when debugging?
  };
  if (unit) {
    object.unit = unit;
  }
  Object.keys(typeTranlations).forEach((t) => {
    if (item[t]) {
      // Sometimes we have the value entered as just an empty string
      if (item[t] && !Number.isNaN(item[t])) {
        if (!newStore[typeTranlations[t].collection]) {
          newStore[typeTranlations[t].collection] = [];
        }
        newStore[typeTranlations[t].collection].push({ ...object, [typeTranlations[t].key]: item[t] });
      }
    }
  });
  if (eventTypeParsers[eventType]) {
    const value = typeof eventTypeParsers[eventType].value === 'function'
      ? eventTypeParsers[eventType].value(item)
      : eventTypeParsers[eventType].value;
    if (!newStore[eventTypeParsers[eventType].collection]) {
      newStore[eventTypeParsers[eventType].collection] = [];
    }
    if (!value) {
      // console.warn('Ignoring item', item);
    } else {
      newStore[eventTypeParsers[eventType].collection].push({ ...object, ...value });
    }
  } else if (eventType) {
    // console.warn('Event type not parsed!', { eventType, item });
  }
  return newStore;
};

const getUrl = ({ collection, params }) => {
  const startTimeString = params.startTime ? `?find[${
    collections[collection].dateField
  }][$gte]=${
    // Need data from previous date too, to be able to display it properly...
    collections[collection].dateFormat(timeDay.offset(params.startTime, -1))
  }` : '';
  const endTimeString = params.endTime ? `${
    startTimeString.length ? '&' : '?'
  }find[${
    collections[collection].dateField
  }][$lte]=${
    collections[collection].dateFormat(params.endTime)
  }` : '';
  const countString = `${
    (startTimeString.length || endTimeString.length) ? '&' : '?'
  }count=${params.count ? params.count : 30000}`;
  return encodeURI(`${Api}/nightscout/${
    collection
  }.json${
    startTimeString
  }${
    endTimeString
  }${
    countString
  }`);
};

const secondsInADay = 60 * 60 * 24;
const timeShiftBasals = (basals, timeshift) => {
  if (!timeshift) {
    return basals;
  }
  const shifted = [...basals];
  if (timeshift > 0) {
    while (shifted[shifted.length - 1].timeAsSeconds + (timeshift * 60) >= secondsInADay) {
      // TODO: is timeshift expressed in minutes???
      const lapped = shifted.pop();
      if (lapped.value !== shifted[0].value) {
        // only add this if required
        lapped.timeAsSeconds -= secondsInADay;
        shifted.unshift(lapped);
      }
    }
  } else if (timeshift < 0) {
    while (shifted[0].timeAsSeconds < -timeshift * 60) {
      // TODO: is timeshift expressed in minutes???
      const lapped = shifted.shift();
      if (lapped.value !== shifted[shifted.length - 1].value) {
        // only add this if required
        lapped.timeAsSeconds += 60 * 60 * 24;
        shifted.push(lapped);
      }
    }
  }
  shifted.forEach((b) => {
    // eslint-disable-next-line no-param-reassign
    b.timeAsSeconds += timeshift;
    const time = b.time.split(':');
    time[0] += Math.floor(timeshift / 60);
    time[1] += timeshift % 60;
  });
  return shifted;
};

// Within the same day!
// Notice the possible timeshift too!
const getBasalRates = (usedProfile, startTime, endTime) => {
  if (startTime > endTime) {
    throw new Error(`${startTime} is after ${endTime}!`);
  }
  const rates = [];
  if (!usedProfile) {
    return rates;
  }
  if (!usedProfile.profile) {
    console.warn('No profile!!!', startTime, endTime, usedProfile);
    return rates;
  }
  const profileSteps = timeShiftBasals(usedProfile.profile.basal, usedProfile.timeshift);
  if (!profileSteps.length) {
    console.warn('No profile steps!!!', startTime, endTime, usedProfile);
    return rates;
  }

  let i = 0;
  let time = startTime;
  while (i < profileSteps.length - 1) {
    const nextTime = profileSteps[i + 1].time.split(':');
    if (nextTime[0] > startTime.getHours()
      || (+nextTime[0] === startTime.getHours() && nextTime[1] > startTime.getMinutes())) {
      // next step has started later than startTime,
      // so the current is the profile step active during startTime
      break;
    }
    i += 1;
  }
  while (i < profileSteps.length) {
    let nextStartTime;
    if (i < profileSteps.length - 1) {
      const nextTime = profileSteps[i + 1].time.split(':');
      nextStartTime = new Date(
        startTime.getFullYear(),
        startTime.getMonth(),
        startTime.getDate(),
        nextTime[0],
        nextTime[1],
        0,
      );
    } else {
      // the last step
      nextStartTime = endTime;
    }
    rates.push({
      time: time.toISOString(),
      endTime: (nextStartTime < endTime ? nextStartTime : endTime).toISOString(),
      rate: profileSteps[i].value * (!usedProfile.percentage || Number.isNaN(usedProfile.percentage)
        ? 1
        : (usedProfile.percentage / 100)),
      profile: usedProfile,
    });
    if (nextStartTime >= endTime) {
      break;
    }
    time = nextStartTime;
    i += 1;
  }
  return rates;
};

let profileFetchPromise;
const getProfiles = () => {
  if (!profileFetchPromise) {
    const profileUseTimeline = [];

    profileFetchPromise = fetch(`${Api}/nightscout/profile.json`, defaultRequestConfig)
      .then(handleError)
      .then(getJson)
      .then((json) => {
        // Parse the stored profile definitions
        json.forEach((profile) => {
          const usedProfile = {
            // eslint-disable-next-line no-underscore-dangle
            id: profile._id,
            time: new Date(profile.startDate).toISOString(),
            // raw: profile,
          };
          if (profile.defaultProfile && profile.store && profile.store[profile.defaultProfile]) {
            usedProfile.name = profile.defaultProfile;
            usedProfile.profile = profile.store[profile.defaultProfile];
          } else {
            usedProfile.profile = profile;
          }
          const storedProfileIndex = bisectTime(profileUseTimeline, usedProfile);
          profileUseTimeline.splice(storedProfileIndex, 0, usedProfile);
      });
      })
      .then(() => fetch(encodeURI(`${Api}/nightscout/treatments.json?&find[eventType]=Profile%20Switch`), defaultRequestConfig))
      .then(handleError)
      .then(getJson)
      .then((json) => {
        // Parse Profile Switch events
        let store = {};
        json.forEach((item) => {
          try {
            store = itemAdapter(item, store);
          } catch (error) {
            console.error('Parse error', { item, error });
          }
        });
        let currentBaseProfile = profileUseTimeline[0];
        if (store.pumpEvents) {
          store.pumpEvents.reverse().forEach((tempProfile) => {
            let storedProfileIndex = bisectTime(profileUseTimeline, tempProfile);
            const current = profileUseTimeline[storedProfileIndex - 1];
            if (!current.endTime || (current.endTime > tempProfile.time)) {
              // need to adjust the previous profile
              current.endTime = tempProfile.time;
            } else if (current.endTime < tempProfile.time) {
              // need to fill in the gap
              const newEntry = {
                ...currentBaseProfile,
                time: current.endTime.toISOString(),
                endTime: tempProfile.time.toISOString(),
              };
              profileUseTimeline.splice(storedProfileIndex, 0, newEntry);
              storedProfileIndex += 1;
            }
            if (!tempProfile.duration) {
              // This is the new base profile
              currentBaseProfile = tempProfile;
            } else {
              // adjust end time
              // eslint-disable-next-line no-param-reassign
              tempProfile.endTime = new Date(new Date(tempProfile.time).getTime()
                + (tempProfile.duration * 60000)).toISOString();
            }
            // Store this profile
            profileUseTimeline.splice(storedProfileIndex, 0, tempProfile);
          });
        }
        if (profileUseTimeline[profileUseTimeline.length - 1].duration) {
          // last added profile was a temp profile, let's add the base profile at the very end
          const lastEntry = {
            ...currentBaseProfile,
            time: profileUseTimeline[profileUseTimeline.length - 1].endTime,
            endTime: new Date().toISOString(),
          };
          profileUseTimeline.push(lastEntry);
        } else {
          // Set the end time for the last profile
          profileUseTimeline[profileUseTimeline.length - 1].endTime = new Date().toISOString();
        }
      })
      .catch((error) => {
        console.error('Unable to fetch profiles from Nightscout', error);
      })
      .then(() => profileUseTimeline);
  }
  return profileFetchPromise;
};

const getBasalsFromProfiles = (profiles, startTime, endTime, tbrEntry) => {
  // Check which is the correct profile - or do we need multiple ones!
  // --> Can a TBR extend beyond profile changes??? Don't think so.

  // We want to split the basals at midnight
  const dates = timeDay.range(startTime, endTime);
  // Add end time
  dates.push(endTime);
  if (startTime < dates[0]) {
    // Add start time, if necessary
    dates.unshift(startTime);
  }
  if (dates.length > 2) {
    // Call this same function recursively for all the days
    let days = [];
    for (let i = 0; i < dates.length - 1; i += 1) {
      days = [...days, ...getBasalsFromProfiles(profiles, dates[i], dates[i + 1], tbrEntry)];
    }
    return days;
  }

  let basals = [];
  const firstProfile = bisectTime(profiles, { time: startTime }) - 1;
  const lastProfile = bisectTime(profiles, { time: endTime }) - 1;
  for (let i = firstProfile; i <= lastProfile; i += 1) {
    const profile = profiles[i];
    if (!profile) {
      continue;
    }
    const myStartTime = (i === firstProfile) ? startTime : new Date(profile.time);
    const myEndTime = (i === lastProfile) ? endTime : new Date(profile.endTime);
    const rates = getBasalRates(profile, myStartTime, myEndTime).map((r, index) => {
      const rate = r.rate * (tbrEntry ? (tbrEntry.percent / 100) : 1);
      const entry = {
        // raw: profile,
        ...tbrEntry,
        ...r,
        id: tbrEntry ? `tbr-${tbrEntry.id}-${index}` : `profile-${profile.id}-${myStartTime.getTime()}`,
        rate,
        amount: rate * timeMinute.count(new Date(r.time), new Date(r.endTime)) / 60,
      };
      if (entry.profile && entry.profile.id) {
        // reduce to just the id
        entry.profile = entry.profile.id;
      }
      return entry;
    });
    basals = [...basals, ...rates];
  }
  // console.log('Processed basals', basals);
  return basals;
};

const parseBasalEntries = (store, params) => getProfiles()
  .then((profiles) => {
    store.profiles = profiles;
    if (store.basal) {
      // process the basals. Add end times, and basal rates from profiles.
      // The retrieved data set may (!) include basals from the end of the previous month.
      // We could take this into consideration.
      // At least, we should know which time frame we are processing the basals for!
      // Then we can track both the profiles and the temp basal settings.
      let { startTime, endTime } = params;
      const now = new Date();
      if (!endTime || endTime > now) {
        endTime = now;
      }
      if (!startTime) {
        startTime = timeMonth(endTime);
      }

      let fixedBasal = [];
      let t = startTime.toISOString();
      while (store.basal.length > 2 && store.basal[store.basal.length - 2].time < t) {
        // reduce extra entries from previous day, only leave the relevant one
        store.basal.pop();
      }
      while (store.basal.length) {
        const b = store.basal.pop(); // reverse order
        if (b.end) {
          b.duration = 0;
          b.rate = 0;
          // A really short entry. We just need a duration later on...
          b.endTime = new Date(new Date(b.time).getTime() + 1).toISOString();
        }
        if (!b.endTime) {
          if (b.duration) {
            b.endTime = new Date(new Date(b.time).getTime() + b.duration * 60000).toISOString();
          } else {
            b.endTime = (store.basal.length > 0)
              ? store.basal[store.basal.length - 1].time
              : new Date().toISOString();
          }
        }

        if (b.time > t) {
          // Need to fill in the gap with profile basals
          const profileBasals = getBasalsFromProfiles(profiles, new Date(t), new Date(b.time));
          fixedBasal = [...fixedBasal, ...profileBasals];
        } else if (b.end || (b.time < t)) {
          // Cancel or edit the last basal!
          // There might be several basal entries from the TBR we're about to cancel
          // Let's erase the ones that never made it
          while (fixedBasal.length && fixedBasal[fixedBasal.length - 1].time > b.time) {
            fixedBasal.pop();
          }
          // And adjust the last one
          if (fixedBasal.length) {
            const previous = fixedBasal[fixedBasal.length - 1];
            if (previous.duration) {
              // Fix the endTime and amount
              if (previous.endTime > b.time) {
                previous.endTime = b.time;
                previous.amount = timeMinute
                  .count(new Date(previous.time), new Date(previous.endTime)) * previous.rate / 60;
              }
            }
          }
          // And restart the usual profile
        }

        if (b.rate) {
          // If the temp basal is set with a fixed rate, add it as it is
          b.amount = b.rate * timeMinute.count(new Date(b.time), new Date(b.endTime)) / 60;
          fixedBasal.push(b);
        } else if (b.percent && !Number.isNaN(b.percent)) {
          // Otherwise add basal entries for each modified step in the current profile
          const adjusted = getBasalsFromProfiles(profiles, new Date(b.time), new Date(b.endTime), b);
          fixedBasal = [...fixedBasal, ...adjusted];
        }
        t = b.endTime;
      }
      // eslint-disable-next-line no-param-reassign
      store.basal = fixedBasal;
    }
    return store;
  });

const join = (a, b) => {
  if (b.raw) {
    if (Array.isArray(a.raw)) {
      a.raw.push(b.raw);
    } else {
      a.raw = [a.raw, b.raw];
    }
  }
  return {
    ...b,
    ...a,
  };
};

const combine = (values) => {
  const map = {};
  values.forEach((v) => {
    const t = isoFormat(parseTime(v));
    if (map[t]) {
      map[t] = join(map[t], v);
    } else {
      map[t] = v;
    }
  });
  return Object.values(map);
};

const checkSync = (a, b) => {
  // Could check in more detail whether the values match...
  if (a.raw && a.raw.enteredBy === 'sync') {
    return b;
  }
  if (b.raw && b.raw.enteredBy === 'sync') {
    return a;
  }
  return join(a, b);
};

const deduplicate = (values) => {
  const map = {};
  values.forEach((v) => {
    const t = isoFormat(parseTime(v));
    if (map[t]) {
      map[t] = checkSync(map[t], v);
    } else {
      map[t] = v;
    }
  });
  return Object.values(map);
};

const fetchData = ({ type: collection, params }) => fetch(
  getUrl({ collection, params }), defaultRequestConfig)
  .then(handleError)
  .then(getJson)
  .then((json) => {
    let store = {};
    json.forEach((item) => {
      store = itemAdapter(item, store);
    });
    if (store.bolus) {
      // Bolus info can come from multiple sources (bolus wizards, etc.)
      // Let's deduplicate the entries
      store.bolus = combine(store.bolus);
    }
    if (store.nutrition) {
      // Also nutrition info can come from multiple sources
      // For instance, from both AAPS and xDrip
      store.nutrition = deduplicate(store.nutrition);
    }
    if (store.pumpEvents || store.basal) {
      // Parse profile changes from pump events.
      // We need these for basal calculations.
      store = parseBasalEntries(store, params);
    }
    return store;
  })
  .then((store => {
    return store;
  }));

export const getNightscoutData = ({ type, params = {} }) => ((dispatch) => {
  const id = idCreator();
  const description = descriptionCreator({ source: 'Nightscout', type, params });
  return checkForCachedDataAction(requestAction({ id, description }), dispatch)
    .then(() => fetchData({ type, params }))
    .then(data => dispatch(nightscoutFetchAction({ id, description, data })))
    .catch(error => dispatch(nightscoutFetchAction({ id, description, error: error.message || error })));
});

export default getNightscoutData;
